import { memo, useCallback, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import { CurrencyProvider } from 'contexts/currencyContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { last } from 'lib/array/arrayUtils'
import { type TransportOption, isCustomFlightSavedItemData, isLeFlightSavedItemData } from 'tripPlanner/api/types'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import { useFlightDetailsForItems } from 'tripPlanner/hooks/api/flights'
import type { RefreshedFlight } from './useRefreshShortlistedFlights'
import useFlightShortlistExpiry from './useFlightShortlistExpiry'
import { isBookedFlightOption, parseSearchParams, resolveShortlistedFlightPriceAndCurrency } from './flightShortlistUtils'
import { getCachedJourney } from './shortlistedJourneyCache'
import { didClickInteractableElement } from 'components/Common/NestableClickable/NestableClickable'
import { useChatStreamStatus } from '../../contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../../contexts/ItineraryContext'
import type { BookFlightResult } from './useBookShortlistedFlight'

const DAY_MONTH_YEAR_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
}

interface Props {
  option: TransportOption
  isBooking: boolean
  isSearching: boolean
  isViewingDetails?: boolean
  refreshedData?: RefreshedFlight
  onBookNow: (option: TransportOption) => void | Promise<BookFlightResult>
  onDelete: (option: TransportOption) => void
  onRefresh: () => void
  onViewBookedDetails?: (option: TransportOption) => void
  onViewUnbookedDetails?: (option: TransportOption, refreshedData?: RefreshedFlight) => void
}

const AirlineLogo = styled.img`
  height: ${rem(32)};
  width: ${rem(32)};
  object-fit: contain;
  flex-shrink: 0;
`

const CardContainer = styled(Sheet)<{ $isExpired: boolean }>`
  opacity: ${props => props.$isExpired ? 0.7 : 1};

  &.is-clickable {
    cursor: pointer;
  }

  &.has-le-flight-hover-border:hover {
    border-color: ${props => props.theme.palette.brand.primary.hover};
  }
`

const Separator = styled.hr`
  border: none;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  margin: 0;
  width: 100%;
`

interface LegData {
  airline?: string
  flightNumber?: string
  departureAirport?: string
  arrivalAirport?: string
  departureTime?: string
  arrivalTime?: string
  duration?: string
  layovers?: Array<{ airportCode?: string; airportName?: string; duration?: string }>
  carrierLogo?: string
}

function formatDurationText(duration: App.HoursMinutes | undefined): string | undefined {
  if (!duration) return undefined
  if (duration.hours === 0) return `${duration.minutes}m`
  if (duration.minutes === 0) return `${duration.hours}h`
  return `${duration.hours}h ${duration.minutes}m`
}

function flightLegToLegData(leg: App.Leg | undefined): LegData | undefined {
  const firstFlight = leg?.flights[0]
  const lastFlight = last(leg?.flights ?? [])

  if (!firstFlight || !lastFlight) return undefined

  return {
    airline: firstFlight.marketingAirline.name,
    flightNumber: firstFlight.flightNumber,
    departureAirport: firstFlight.departureAirport,
    arrivalAirport: lastFlight.arrivalAirport,
    departureTime: firstFlight.departingTime,
    arrivalTime: lastFlight.arrivalTime,
    duration: formatDurationText(leg?.totalTravelDuration),
    carrierLogo: firstFlight.marketingAirline.logoUrl,
  }
}

function getFlightDetailsLegData(
  flightDetails: Array<App.FlightOrderItemDetails> | undefined,
  requestedItemIds: Array<string>,
  orderFlightItemId: string | undefined,
  preferredLegIndex: number | undefined,
  fallbackLegIndex: number,
): LegData | undefined {
  if (!orderFlightItemId) return undefined

  const requestedIndex = requestedItemIds.indexOf(orderFlightItemId)
  const details = requestedIndex >= 0 ? flightDetails?.[requestedIndex] : undefined
  const legs = details?.bookingJourney.legs
  if (!legs?.length) return undefined

  const selectedLeg = typeof preferredLegIndex === 'number' && legs[preferredLegIndex] ?
    legs[preferredLegIndex] :
    legs[fallbackLegIndex] ?? legs[0]

  return flightLegToLegData(selectedLeg)
}

function mergeLegData(primary: LegData, fallback: LegData | undefined): LegData {
  if (!fallback) return primary

  return {
    airline: primary.airline || fallback.airline,
    flightNumber: primary.flightNumber || fallback.flightNumber,
    departureAirport: primary.departureAirport || fallback.departureAirport,
    arrivalAirport: primary.arrivalAirport || fallback.arrivalAirport,
    departureTime: primary.departureTime || fallback.departureTime,
    arrivalTime: primary.arrivalTime || fallback.arrivalTime,
    duration: primary.duration || fallback.duration,
    layovers: primary.layovers ?? fallback.layovers,
    carrierLogo: primary.carrierLogo || fallback.carrierLogo,
  }
}

function formatTimeDisplay(timeString: string): string {
  // Extract the HH:mm portion regardless of separator ("...T16:40", "... 16:40", "16:40").
  const match = timeString.match(/(\d{1,2}):(\d{2})/)
  if (match) {
    return `${match[1].padStart(2, '0')}:${match[2]}`
  }
  return timeString
}

function formatOccupantsLabel(occupancies: Array<{ adults?: number; children?: number; infants?: number }> | undefined, intl: ReturnType<typeof useIntl>): string {
  if (!occupancies?.length) return ''
  const occ = occupancies[0]
  const adults = occ.adults ?? 1
  const children = occ.children ?? 0
  const parts: Array<string> = []

  parts.push(intl.formatMessage(
    {
      id: 'sdK1Uf7spfyoDSVQjFtg',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
      description: 'Passenger count for shortlisted flight card header',
    },
    { count: adults },
  ))

  if (children > 0) {
    parts.push(intl.formatMessage(
      {
        id: '4mwNYERvlKnis5GUVpUB',
        defaultMessage: '{count, plural, one {# child} other {# children}}',
        description: 'Children passenger count displayed in the shortlisted flight card header section',
      },
      { count: children },
    ))
  }

  return parts.join(', ')
}

interface FlightLegRowProps {
  leg: LegData
}

function FlightLegRow({ leg }: FlightLegRowProps) {
  const departureTime = leg.departureTime ? formatTimeDisplay(leg.departureTime) : '--:--'
  const arrivalTime = leg.arrivalTime ? formatTimeDisplay(leg.arrivalTime) : '--:--'
  const routeLabel = `${leg.departureAirport ?? ''}-${leg.arrivalAirport ?? ''}`
  const flightTimeRange = `${departureTime} - ${arrivalTime}`
  return (
    <Group direction="horizontal" gap={16} verticalAlign="center" horizontalAlign="space-between" fullWidth>
      <Group direction="horizontal" gap={24} verticalAlign="center" shrink={false}>
        {leg.carrierLogo && (
          <AirlineLogo src={leg.carrierLogo} alt={leg.airline ?? ''} />
        )}
        <Group direction="vertical" gap={2}>
          <Heading variant="heading6">{flightTimeRange}</Heading>
          <Caption variant="medium" colour="light-foreground-alt">{leg.airline ?? ''}</Caption>
        </Group>
        <Group direction="vertical" gap={2} shrink={false}>
          <BodyText variant="medium" weight="bold">{leg.duration ?? ''}</BodyText>
          <Caption variant="medium" colour="light-foreground-alt">{routeLabel}</Caption>
        </Group>
      </Group>
    </Group>
  )
}

function ShortlistedFlightCard({
  option,
  isBooking,
  isSearching,
  isViewingDetails = false,
  refreshedData,
  onBookNow,
  onDelete,
  onRefresh,
  onViewBookedDetails,
  onViewUnbookedDetails,
}: Props) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const { isManualItineraryDeletePending } = useItineraryContext()
  const deleteActionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  const savedItemData = option.savedItemData
  const isLeFlightData = isLeFlightSavedItemData(savedItemData)
  const isCustomFlightData = isCustomFlightSavedItemData(savedItemData)
  const leFlightData = isLeFlightData ? savedItemData : undefined
  const customFlightData = isCustomFlightData ? savedItemData : undefined
  const isSoldOut = isLeFlightData && (refreshedData?.isSoldOut ?? false)
  const effectiveExpiry = isLeFlightData
    ? refreshedData?.newExpiry ?? leFlightData?.searchExpiry
    : undefined
  const { isExpired: timerExpired } = useFlightShortlistExpiry(effectiveExpiry)
  const isExpired = isLeFlightData && (isSoldOut || timerExpired)
  const isBooked = isBookedFlightOption(option)
  const shouldShowExpiredBanner = isExpired && !isBooked
  const shouldShowBookNow = !isExpired && !isBooked
  const shouldShowBookedTag = !shouldShowBookNow && isBooked

  const returnLeg = option.returnLeg
  const returnLegData = isLeFlightSavedItemData(returnLeg?.savedItemData) ? returnLeg.savedItemData : undefined
  const isReturn = !!returnLeg && !!returnLegData
  const leFlightOrderFlightItemId = leFlightData?.orderFlightItemId
  const leFlightOrderId = leFlightData?.orderId
  const bookedFlightItemIds = useMemo(() => {
    if (!isBooked || !isLeFlightData) return []

    return [
      leFlightOrderFlightItemId,
      returnLegData?.orderFlightItemId,
    ].filter((itemId, index, itemIds): itemId is string =>
      !!itemId && itemIds.indexOf(itemId) === index,
    )
  }, [isBooked, isLeFlightData, leFlightOrderFlightItemId, returnLegData?.orderFlightItemId])
  const bookedFlightOrderId = isBooked && isLeFlightData ?
    leFlightOrderId ?? returnLegData?.orderId ?? '' :
    ''
  const { data: bookedFlightDetails } = useFlightDetailsForItems({
    orderId: bookedFlightOrderId,
    itemIds: bookedFlightItemIds,
    enabled: bookedFlightOrderId.length > 0 && bookedFlightItemIds.length > 0,
  })

  const hydratedDepartingJourney = useMemo(() => {
    const fromRefresh = refreshedData?.matchedDepartingJourney ?? refreshedData?.matchedJourney
    if (fromRefresh) {
      return fromRefresh
    }

    if (!leFlightData?.journeyKey) {
      return undefined
    }

    return getCachedJourney(leFlightData.journeyKey)?.journey
  }, [leFlightData?.journeyKey, refreshedData?.matchedDepartingJourney, refreshedData?.matchedJourney])

  const hydratedReturningJourney = useMemo(() => {
    const fromRefresh = refreshedData?.matchedReturningJourney
    if (fromRefresh) {
      return fromRefresh
    }

    if (!isLeFlightData || !returnLegData?.journeyKey) {
      return undefined
    }

    return getCachedJourney(returnLegData.journeyKey)?.journey
  }, [isLeFlightData, refreshedData?.matchedReturningJourney, returnLegData?.journeyKey])

  const canViewUnbookedDetails = useMemo(() => {
    if (isBooked || !isLeFlightData || !onViewUnbookedDetails || isViewingDetails || isSoldOut) {
      return false
    }

    if (isReturn) {
      return Boolean(hydratedDepartingJourney && hydratedReturningJourney)
    }

    return Boolean(hydratedDepartingJourney)
  }, [isBooked, isLeFlightData, onViewUnbookedDetails, isViewingDetails, isSoldOut, isReturn, hydratedDepartingJourney, hydratedReturningJourney])

  const canViewBookedDetails = useMemo(() => {
    if (!isBooked || !isLeFlightData || !onViewBookedDetails || isViewingDetails) {
      return false
    }

    const orderId = leFlightData?.orderId ?? returnLegData?.orderId
    const orderFlightItemId = leFlightData?.orderFlightItemId ?? returnLegData?.orderFlightItemId

    return Boolean(orderId && orderFlightItemId)
  }, [isBooked, isLeFlightData, isViewingDetails, leFlightData?.orderFlightItemId, leFlightData?.orderId, onViewBookedDetails, returnLegData?.orderFlightItemId, returnLegData?.orderId])

  const canViewDetails = canViewBookedDetails || canViewUnbookedDetails
  // A full-card click only opens flight details. Booking stays on the explicit "Book now"
  // button so a normal card click can never start a booking when details are unavailable.
  const isCardClickable = canViewDetails

  const toggleDropdown = useCallback(() => {
    if (deleteActionsDisabled) return
    setIsDropdownOpen(prev => !prev)
  }, [deleteActionsDisabled])

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const handleDelete = useCallback(() => {
    if (deleteActionsDisabled) return
    closeDropdown()
    onDelete(option)
  }, [deleteActionsDisabled, closeDropdown, onDelete, option])

  const handleBookNow = useCallback(() => {
    onBookNow(option)
  }, [onBookNow, option])

  const handleViewBookedDetails = useCallback(() => {
    if (canViewBookedDetails && onViewBookedDetails) {
      onViewBookedDetails(option)
      return
    }

    if (!canViewUnbookedDetails || !onViewUnbookedDetails) {
      return
    }

    onViewUnbookedDetails(option, refreshedData)
  }, [canViewBookedDetails, onViewBookedDetails, option, canViewUnbookedDetails, onViewUnbookedDetails, refreshedData])

  const activateCard = useCallback(() => {
    if (canViewDetails) {
      handleViewBookedDetails()
    }
  }, [canViewDetails, handleViewBookedDetails])

  const handleCardClick = useCallback((event: MouseEvent<HTMLElement>) => {
    if (!isCardClickable || !(event.target instanceof HTMLElement)) {
      return
    }

    if (didClickInteractableElement(event.target, event.currentTarget)) {
      return
    }

    activateCard()
  }, [isCardClickable, activateCard])

  const handleCardKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (!isCardClickable || !(event.target instanceof HTMLElement)) {
      return
    }

    if (didClickInteractableElement(event.target, event.currentTarget)) {
      return
    }

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      activateCard()
    }
  }, [isCardClickable, activateCard])

  const bookNowLabel = intl.formatMessage({
    id: '4VTNHzs2wLQP5XngJurI',
    defaultMessage: 'Book now',
    description: 'Button label to book a shortlisted flight in the trip planner itinerary',
  })

  const checkingAvailabilityLabel = intl.formatMessage({
    id: 'MWzt6RxwpAuF9uQV3G55',
    defaultMessage: 'Checking availability...',
    description: 'Loading text shown while re-searching flight availability before booking',
  })

  const searchingNode = useMemo<ReactNode>(() => (
    <Group direction="horizontal" gap={8} verticalAlign="center">
      <LoadingSpinner size={16} inline />
      <Caption variant="medium" colour="light-foreground-alt">{checkingAvailabilityLabel}</Caption>
    </Group>
  ), [checkingAvailabilityLabel])

  const bookNowButton = useMemo<ReactNode>(() => (
    <TextButton kind="primary" size="small" onClick={handleBookNow} disabled={isBooking}>
      {bookNowLabel}
    </TextButton>
  ), [handleBookNow, isBooking, bookNowLabel])

  const bookingActionNode = useMemo<ReactNode>(() => {
    if (shouldShowBookNow) {
      return isSearching ? searchingNode : bookNowButton
    }

    if (shouldShowBookedTag) {
      return <BookedLabel />
    }

    return null
  }, [bookNowButton, isSearching, searchingNode, shouldShowBookNow, shouldShowBookedTag])

  const searchParams = useMemo(() =>
    leFlightData ? parseSearchParams(leFlightData.searchParams) : undefined,
  [leFlightData])

  const subtitle = useMemo(() => {
    if (!isLeFlightData && !isCustomFlightData) return ''

    if (!isLeFlightData) {
      if (customFlightData?.departureAirport && customFlightData.arrivalAirport) {
        return intl.formatMessage(
          {
            id: 'pmeuxOyYKH5ZCNDyJkFw',
            defaultMessage: '{origin} to {destination}',
            description: 'Flight route label showing origin and destination airports in shortlisted flight card subtitle',
          },
          {
            origin: customFlightData.departureAirport,
            destination: customFlightData.arrivalAirport,
          },
        )
      }
      return ''
    }

    const parts: Array<string> = []

    if (searchParams?.itinerary?.length) {
      const firstLeg = searchParams.itinerary[0]
      const lastLeg = searchParams.itinerary[searchParams.itinerary.length - 1]
      const departDate = dateFormatter(firstLeg.departureDate, DAY_MONTH_YEAR_FORMAT)
      if (isReturn) {
        const returnDate = dateFormatter(lastLeg.departureDate, DAY_MONTH_YEAR_FORMAT)
        parts.push(`${departDate} - ${returnDate}`)
      } else {
        parts.push(departDate)
      }
      parts.push(intl.formatMessage(
        {
          id: 'pmeuxOyYKH5ZCNDyJkFw',
          defaultMessage: '{origin} to {destination}',
          description: 'Flight route label showing origin and destination airports in shortlisted flight card subtitle',
        },
        {
          origin: leFlightData?.departureAirport ?? firstLeg.departureAirport,
          destination: leFlightData?.arrivalAirport ?? firstLeg.arrivalAirport,
        },
      ))
    }

    const occupantsLabel = formatOccupantsLabel(searchParams?.occupancies, intl)
    if (occupantsLabel) {
      parts.push(occupantsLabel)
    }

    return parts.join(' · ')
  }, [customFlightData, dateFormatter, isLeFlightData, isCustomFlightData, leFlightData, searchParams, isReturn, intl])

  const flightSavedItemData = leFlightData ?? customFlightData
  if (!flightSavedItemData) return null

  const headerTitle = isReturn
    ? intl.formatMessage({
        id: 'mLIlOWUKrurIn4sOwr09',
        defaultMessage: 'Return flights',
        description: 'Header title for a return flight card in the shortlisted flights section',
      })
    : intl.formatMessage({
        id: 'bnPie_corRQMr-f_TMzG',
        defaultMessage: 'One-way flight',
        description: 'Header title for a one-way flight card in the shortlisted flights section',
      })

  const hydratedOutboundLeg = getFlightDetailsLegData(
    bookedFlightDetails,
    bookedFlightItemIds,
    leFlightData?.orderFlightItemId,
    leFlightData?.legOrder,
    0,
  )
  const hydratedInboundLeg = isReturn ? getFlightDetailsLegData(
    bookedFlightDetails,
    bookedFlightItemIds,
    returnLegData.orderFlightItemId,
    returnLegData.legOrder,
    leFlightData?.orderFlightItemId === returnLegData.orderFlightItemId ? 1 : 0,
  ) : undefined

  const outboundLeg = mergeLegData({
    airline: flightSavedItemData.airline,
    flightNumber: flightSavedItemData.flightNumber,
    departureAirport: flightSavedItemData.departureAirport,
    arrivalAirport: flightSavedItemData.arrivalAirport,
    departureTime: flightSavedItemData.departureTime,
    arrivalTime: flightSavedItemData.arrivalTime,
    duration: flightSavedItemData.duration,
    layovers: leFlightData?.layovers,
    carrierLogo: flightSavedItemData.carrierLogo ?? leFlightData?.carrierLogo,
  }, hydratedOutboundLeg)

  const inboundLeg: LegData | undefined = isReturn ? mergeLegData({
    airline: returnLegData.airline,
    flightNumber: returnLegData.flightNumber,
    departureAirport: returnLegData.departureAirport,
    arrivalAirport: returnLegData.arrivalAirport,
    departureTime: returnLegData.departureTime,
    arrivalTime: returnLegData.arrivalTime,
    duration: returnLegData.duration,
    layovers: returnLegData.layovers,
    carrierLogo: returnLegData.carrierLogo,
  }, hydratedInboundLeg) : undefined

  const { displayPrice, priceCurrency } = resolveShortlistedFlightPriceAndCurrency({
    isReturn,
    savedItemData: leFlightData,
    refreshedData,
  })

  const deleteLabel = intl.formatMessage({
    id: 'tIqA2l1Ir8TxtJVcN5iz',
    defaultMessage: 'Delete',
    description: 'Dropdown menu item to delete a shortlisted flight from the trip planner',
  })

  const expiredMessage = intl.formatMessage({
    id: 'XtEObgroFhJOEg_T8Xs8',
    defaultMessage: 'This flight price has expired',
    description: 'Per-card banner message shown when an individual shortlisted flight price has expired',
  })

  const refreshLabel = intl.formatMessage({
    id: '7fxey21pp4EkuxxhENyy',
    defaultMessage: 'Refresh',
    description: 'Button to refresh an individual expired shortlisted flight price in trip planner',
  })

  const kebabMenu = (
    <>
      <IconButton
        size="small"
        kind="tertiary"
        onClick={toggleDropdown}
        ref={dropdownTriggerRef}
        disabled={deleteActionsDisabled}
      >
        <LineEllipsisVIcon />
      </IconButton>
      <DropdownList
        size="S"
        open={isDropdownOpen}
        onClose={closeDropdown}
        triggerRef={dropdownTriggerRef}
        anchorRef={dropdownTriggerRef}
        placement="bottom-end"
        portaled
      >
        <ClickableListItem
          title={deleteLabel}
          size="medium"
          startIcon={<LineTrashAltIcon size="S" />}
          onClick={handleDelete}
        />
      </DropdownList>
    </>
  )

  const shouldShowBookedTagInReturnHeader = isReturn && shouldShowBookedTag
  const shouldShowBottomDivider = !shouldShowBookedTagInReturnHeader
  const bookingActionNodeInReturnFooter = shouldShowBookedTagInReturnHeader ? null : bookingActionNode
  // Refresh pair wins when present; otherwise both fields come from saved data.
  const canRenderPrice = displayPrice != null && Boolean(priceCurrency)
  const hasBookingFooter = isLeFlightData && (canRenderPrice || bookingActionNodeInReturnFooter !== null)
  const returnContent = (
    <Group direction="vertical" gap={16} fullWidth>
      <Group direction="horizontal" gap={8} verticalAlign="start" horizontalAlign="space-between" fullWidth>
        <Group direction="vertical" gap={4}>
          <Heading variant="heading6">{headerTitle}</Heading>
          <Caption variant="medium" colour="light-foreground-alt">{subtitle}</Caption>
        </Group>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          {shouldShowBookedTagInReturnHeader && <BookedLabel />}
          {kebabMenu}
        </Group>
      </Group>

      <Separator />

      <FlightLegRow leg={outboundLeg} />
      <FlightLegRow leg={inboundLeg!} />

      {hasBookingFooter && <>
        {shouldShowBottomDivider && <Separator />}

        <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign={canRenderPrice ? 'space-between' : 'end'} fullWidth>
          {canRenderPrice && (
            <CurrencyProvider currency={priceCurrency}>
              <PriceRowPrice size="M" price={displayPrice} saleUnit="adult" />
            </CurrencyProvider>
          )}
          {bookingActionNodeInReturnFooter}
        </Group>
      </>}
    </Group>
  )

  const onewayContent = (
    <Group direction="horizontal" gap={16} verticalAlign="center" horizontalAlign="space-between" fullWidth>
      <FlightLegRow leg={outboundLeg} />

      {isLeFlightData && canRenderPrice && (
        <CurrencyProvider currency={priceCurrency}>
          <Group direction="vertical" gap={0} shrink={false}>
            <PriceRowPrice size="M" price={displayPrice} saleUnit="adult" />
          </Group>
        </CurrencyProvider>
      )}

      <Group direction="horizontal" gap={8} verticalAlign="center" shrink={false}>
        {isLeFlightData && bookingActionNode}
        {kebabMenu}
      </Group>
    </Group>
  )

  return (
    <CardContainer
      border="light-foreground"
      borderRadius="L"
      padding={16}
      $isExpired={isExpired}
      className={clsx({
        'is-clickable': isCardClickable,
        'has-le-flight-hover-border': isLeFlightData,
      })}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={isCardClickable ? 'button' : undefined}
      tabIndex={isCardClickable ? 0 : undefined}
    >
      <Group direction="vertical" gap={12} fullWidth>
        {isReturn ? returnContent : onewayContent}
        {shouldShowExpiredBanner && (
          <MessageBanner
            kind="warning"
            density="compact"
            icon={<LineInfoCircleIcon size="S" />}
            primaryActionButtonProps={{
              children: refreshLabel,
              onClick: onRefresh,
              size: 'small',
            }}
          >
            {expiredMessage}
          </MessageBanner>
        )}
      </Group>
    </CardContainer>
  )
}

export default memo(ShortlistedFlightCard)
